<template>
  <div class="bg-white">
    <!-- Header Section with Green Background -->
    <div class="bg-gradient-to-r from-green-100 to-green-200 px-8 py-6 text-center">
      <div class="flex items-center justify-center gap-3">
        <div class="rounded-full p-2">
          <img :src="Image('integrations/google-sheets.png')" class="w-16 h-auto" alt="Google Sheets">
        </div>
        <h1 class="text-3xl text-gray-800 font-bold">
          {{ t('google_sheets_title') }}
        </h1>
      </div>
    </div>

    <!-- Content Section -->
    <div class="px-8 py-8">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-8">
        <svg class="animate-spin w-8 h-8 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <template v-else>
        <!-- Not Connected / Revoked State -->
        <template v-if="!isConnected || isRevoked">
          <!-- Title with Watch Tutorial -->
          <div class="flex items-center gap-4 mb-2">
            <h2 class="text-2xl font-bold text-gray-800">{{ t('connect_google_sheets') }}</h2>
            <a
              href="https://www.youtube.com/watch?v=x70qZf2KWwg"
              target="_blank"
              class="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 transition duration-150 rounded-full px-4 py-1.5 border border-gray-200 text-sm font-medium text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="18" height="18" viewBox="0 0 48 48">
                <path fill="#FF3D00" d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z"></path>
                <path fill="#FFF" d="M20 31L20 17 32 24z"></path>
              </svg>
              {{ t('watch_tutorial') }}
            </a>
          </div>

          <p class="text-gray-600 mb-6 leading-relaxed" v-html="t('connect_google_desc', { link: `<a href='https://sheets.google.com' target='_blank' class='text-blue-600 hover:underline'>Google Sheets</a>` })"></p>

          <!-- Revoked State Warning -->
          <div v-if="isRevoked" class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
            <svg class="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            <span class="text-sm text-yellow-800" v-html="t('connection_expired', { account: connectedEmail || t('your_account') })"></span>
          </div>

          <!-- Connect Button -->
          <div class="flex items-center gap-3">
            <button
              @click="isRevoked ? handleReconnect() : handleConnect()"
              class="inline-flex items-center gap-3 bg-white hover:bg-gray-50 border border-gray-300 hover:border-gray-400 text-gray-700 font-medium px-5 py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow"
            >
              <svg viewBox="0 0 24 24" class="w-5 h-5">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {{ t('continue_with_google') }}
            </button>
            <div class="relative group">
              <button type="button" class="text-gray-400 hover:text-gray-600">
                <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
              </button>
              <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                {{ t('google_oauth_tooltip') }}
                <div class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Connected State -->
        <template v-else>
          <!-- Account Connected Section -->
          <div class="mb-6">
            <p class="text-gray-700 font-medium mb-2">{{ t('account_connected_emoji') }}</p>
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2 px-4 py-2 border border-teal-400 rounded-lg bg-white">
                <div v-if="isFetchingUserInfo" class="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center">
                  <svg class="animate-spin w-3 h-3 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <img v-else-if="connectedPicture" :src="connectedPicture" :alt="connectedEmail" class="w-5 h-5 rounded-full object-cover" referrerpolicy="no-referrer" />
                <svg v-else class="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                <span class="text-sm text-teal-700 font-medium">{{ connectedEmail || t('connected') }}</span>
              </div>
              <button
                @click="showRemoveConfirmation = true"
                type="button"
                class="text-gray-400 hover:text-gray-600 text-sm font-medium transition"
              >
                {{ t('disconnect') }}
              </button>
            </div>
          </div>

          <!-- Spreadsheet Selection -->
          <div class="mb-4">
            <!-- Spreadsheet Picker (no sheet selected or changing) -->
            <div v-if="!syncSettings.spreadsheet_id || isChangingSheet">
              <template v-if="!showCreateMode">
                <InputLabel class="mb-2 font-bold">{{ t('choose_spreadsheet') }}</InputLabel>
                <div class="flex gap-2 items-center">
                  <Select
                    v-model="selectedSheetId"
                    :options="spreadsheetOptions"
                    :placeholder="t('choose_one')"
                    :search="t('search_spreadsheets')"
                    :not_found="isLoadingSheets ? t('loading') : t('no_spreadsheets_found')"
                    size="w-full"
                    class="flex-1"
                    white
                  />
                  <Button @click="confirmSelectSheet" :disabled="!selectedSheetId || isSelectingSheet">
                    {{ isSelectingSheet ? t('connecting') : t('connect') }}
                  </Button>
                </div>
                <p class="mt-2 text-sm text-orange-500 italic">{{ t('select_sheet_warning') }}</p>
                <div class="mt-4">
                  <span class="text-gray-500 text-sm italic">{{ t('or') }},</span>
                  <button @click="showCreateMode = true" type="button" class="mt-2 flex items-center gap-1 px-4 py-2 border border-indigo-300 rounded-lg text-indigo-600 hover:bg-indigo-50 text-sm font-medium transition">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                    </svg>
                    {{ t('create_new') }}
                  </button>
                </div>
              </template>
              <template v-else>
                <InputLabel class="mb-2 font-bold">{{ t('enter_spreadsheet_name') }}</InputLabel>
                <div class="flex gap-2 items-center" :class="{ 'opacity-50 pointer-events-none': isCreatingSheet }">
                  <Text v-model="newSheetName" :placeholder="t('formychat_leads_placeholder')" white class="flex-1" :readonly="isCreatingSheet" />
                  <button
                    @click="createNewSheet"
                    :disabled="!newSheetName || isCreatingSheet"
                    class="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    {{ isCreatingSheet ? t('creating') : t('create_new') }}
                  </button>
                </div>
                <div class="mt-3">
                  <span class="text-gray-500 text-sm italic">{{ t('or') }}, </span>
                  <button @click="showCreateMode = false" type="button" class="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                    {{ t('select_existing') }}
                  </button>
                </div>
              </template>
            </div>

            <!-- Selected Spreadsheet Display -->
            <div v-else>
              <InputLabel class="mb-2 font-bold">{{ t('choose_spreadsheet') }}</InputLabel>
              <div class="flex items-center gap-3 px-4 py-3 border border-green-200 rounded-lg bg-green-50">
                <img :src="Image('integrations/google-sheets.png')" class="w-6 h-auto" alt="Google Sheets">
                <span class="flex-1 text-gray-800 truncate">{{ syncSettings.spreadsheet_name || syncSettings.spreadsheet_id }}</span>
                <a :href="'https://docs.google.com/spreadsheets/d/' + syncSettings.spreadsheet_id" target="_blank" class="text-teal-600 hover:text-teal-700 text-sm font-medium">{{ t('open') }}</a>
                <button @click="isChangingSheet = true" type="button" class="text-gray-400 hover:text-gray-600 text-sm">{{ t('change') }}</button>
              </div>
            </div>
          </div>

          <!-- Sync Error/Success Messages (always visible) -->
          <div v-if="syncError" class="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm">{{ syncError }}</div>
          <div v-if="syncSuccess" class="mb-4 p-2 bg-green-50 border border-green-200 rounded text-green-800 text-sm">{{ syncSuccess }}</div>

          <!-- Sync Configuration (only show when spreadsheet is selected) -->
          <template v-if="syncSettings.spreadsheet_id && !isChangingSheet">
            <!-- Sync Settings Row -->
            <div class="flex gap-4 items-end justify-start mb-6 transition-opacity duration-200" :class="{ 'opacity-50 pointer-events-none': isSaving || isSyncing || isAutoSaving }">
              <div>
                <InputLabel class="mb-2 font-bold">{{ t('sync_frequency') }}</InputLabel>
                <Select v-model="syncSettings.sync_mode" :options="syncModeOptions" :placeholder="t('select_sync_mode')" :search="false" white :readonly="isSaving || isSyncing || isAutoSaving" />
              </div>
              <div v-if="syncSettings.sync_mode === 'scheduled'">
                <InputLabel class="mb-2 font-bold">{{ t('sync_interval') }}</InputLabel>
                <Select v-model="syncSettings.sync_interval" :options="intervalOptions" :placeholder="t('select_interval')" :search="false" white :readonly="isSaving || isSyncing || isAutoSaving" />
              </div>
            </div>

            <!-- Settings Messages -->
            <div v-if="settingsSuccess" class="mb-4 p-2 bg-green-50 border border-green-200 rounded text-green-800 text-sm">{{ settingsSuccess }}</div>
            <div v-if="settingsError" class="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm">{{ settingsError }}</div>

            <!-- Save & Sync Button (before initial sync) -->
            <div v-if="!isInitialSyncDone" class="flex justify-end">
              <button
                @click="saveAndSync"
                :disabled="isSaving || isSyncing"
                class="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-lg transition disabled:opacity-75 disabled:cursor-not-allowed"
              >
                <svg v-if="isSaving || isSyncing" class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isSaving || isSyncing ? t('saving') : t('save_and_sync') }}
              </button>
            </div>

            <!-- Sync Status (only after initial sync is done) -->
            <template v-else>
              <div class="flex items-center justify-between pt-4 border-t border-gray-200">
                <div class="flex items-center gap-4 text-sm">
                  <span><span class="font-semibold text-green-600">{{ syncStats.synced }}</span> {{ t('synced') }}</span>
                  <span><span class="font-semibold text-yellow-600">{{ syncStats.pending }}</span> {{ t('pending') }}</span>
                  <span class="text-gray-400">{{ t('last') }}: {{ formattedLastSync }}</span>
                </div>
                <button
                  @click="triggerManualSync"
                  :disabled="isSyncing"
                  class="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded transition-all duration-200"
                  :class="{
                    'bg-green-600 hover:bg-green-700 text-white': !syncButtonState,
                    'bg-green-600 text-white': isSyncing,
                    'bg-green-100 text-green-700 border border-green-300': syncButtonState === 'synced'
                  }"
                >
                  <svg v-if="syncButtonState === 'synced'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <svg v-else class="w-4 h-4" :class="{ 'animate-spin': isSyncing }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                  {{ isSyncing ? t('syncing') : (syncButtonState === 'synced' ? t('synced_exclamation') : t('sync_now')) }}
                </button>
              </div>

              <div v-if="!isPremium && syncStats.synced >= freeLimit" class="mt-4 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                {{ t('free_version_limit_reached', { limit: freeLimit }) }}
              </div>
            </template>
          </template>
        </template>

        <!-- Connection Status Messages -->
        <div v-if="connectionStatus === 'error'" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <svg class="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
          <p class="text-red-800 text-sm font-medium">{{ errorMessage || t('connection_failed_try_again') }}</p>
        </div>
      </template>

      <!-- Disconnect Confirmation Dialog -->
      <div v-if="showRemoveConfirmation" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <h3 class="text-xl font-semibold text-gray-800 mb-3">{{ t('disconnect_google_account') }}</h3>
          <p class="text-gray-600 mb-6">{{ t('disconnect_google_warning') }}</p>
          <div class="flex justify-end gap-3">
            <button
              @click="showRemoveConfirmation = false"
              :disabled="isDisconnecting"
              class="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-lg transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ t('cancel') }}
            </button>
            <button
              @click="handleDisconnect"
              :disabled="isDisconnecting"
              class="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span v-if="isDisconnecting" class="inline-block w-5 h-5">
                <svg class="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              <span v-if="!isDisconnecting">{{ t('yes_disconnect') }}</span>
              <span v-else>{{ t('disconnecting') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Resync Confirmation Dialog -->
      <div v-if="showResyncConfirmation" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <h3 class="text-xl font-semibold text-gray-800 mb-3">{{ t('full_resync') }}</h3>
          <p class="text-gray-600 mb-4">{{ t('full_resync_warning') }}</p>
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
            <p class="text-yellow-800 text-sm">{{ t('backup_spreadsheet_warning') }}</p>
          </div>
          <div class="flex justify-end gap-3">
            <button
              @click="showResyncConfirmation = false"
              class="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-lg transition duration-150"
            >
              {{ t('cancel') }}
            </button>
            <button
              @click="executeFullResync"
              class="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg transition duration-150"
            >
              {{ t('yes_resync') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@util/API'
import { Select, Button, Text, InputLabel } from '@components'
import { useTranslation } from '../../composables/useTranslation'

export default {
  name: 'GoogleSheetsSetup',
  components: { Select, Button, Text, InputLabel },
  setup() {
    const { t } = useTranslation()
    return { t }
  },
  data() {
    return {
      isLoading: true,
      isFetchingUserInfo: false,
      isConnected: false,
      isRevoked: false,
      connectedEmail: '',
      connectedPicture: '',
      isDisconnecting: false,
      showRemoveConfirmation: false,
      connectionStatus: null,
      errorMessage: '',
      // Sync configuration
      syncSettings: {
        spreadsheet_id: '',
        spreadsheet_name: '',
        sync_mode: 'scheduled',
        sync_interval: '1h',
        last_sync_at: null,
      },
      syncStats: {
        total: 0,
        synced: 0,
        pending: 0,
      },
      freeLimit: 100,
      intervals: [],
      // Spreadsheet selection
      spreadsheets: [],
      isLoadingSheets: false,
      showCreateMode: false,
      selectedSheetId: '',
      isSelectingSheet: false,
      newSheetName: '',
      isCreatingSheet: false,
      isChangingSheet: false,
      showResyncConfirmation: false,
      isSaving: false,
      isSyncing: false,
      syncError: '',
      syncSuccess: '',
      settingsSuccess: '',
      settingsError: '',
      isInitialSyncDone: false,
      syncButtonState: null,
      isAutoSaving: false,
      isDataLoaded: false,
    }
  },
  props: {
    integration: {
      type: Object,
      required: true
    }
  },
  computed: {
    isPremium() {
      return window.formychat_admin_vars?.is_premium || false
    },
    spreadsheetOptions() {
      return this.spreadsheets.map(s => ({
        value: s.id,
        label: s.name,
        name: s.name
      }))
    },
    syncModeOptions() {
      return [
        { value: 'scheduled', label: this.t('scheduled_recommended'), name: this.t('scheduled_recommended') },
        { value: 'realtime', label: this.t('real_time'), name: this.t('real_time') },
        { value: 'manual', label: this.t('manual'), name: this.t('manual') }
      ]
    },
    intervalOptions() {
      return this.intervals.map(i => ({
        value: i.value,
        label: i.label,
        name: i.label
      }))
    },
    formattedLastSync() {
      if (!this.syncSettings.last_sync_at) {
        return this.t('never')
      }

      // Parse MySQL datetime format (YYYY-MM-DD HH:MM:SS)
      const dateStr = this.syncSettings.last_sync_at.replace(' ', 'T')
      const lastSync = new Date(dateStr)

      if (isNaN(lastSync.getTime())) {
        return this.syncSettings.last_sync_at
      }

      const now = new Date()
      const diffMs = now - lastSync
      const diffMins = Math.floor(diffMs / 60000)

      if (diffMins < 1) {
        return this.t('just_now')
      } else if (diffMins < 60) {
        if (diffMins === 1) return this.t('minute_ago')
        return this.t('minutes_ago', { minutes: diffMins })
      } else {
        return lastSync.toLocaleString()
      }
    }
  },
  mounted() {
    this.loadConnectionStatus()
  },
  watch: {
    'syncSettings.sync_mode': {
      handler(newVal, oldVal) {
        if (this.isDataLoaded && newVal !== oldVal && this.isInitialSyncDone && this.syncSettings.spreadsheet_id && !this.isAutoSaving) {
          this.autoSaveSettings()
        }
      }
    },
    'syncSettings.sync_interval': {
      handler(newVal, oldVal) {
        if (this.isDataLoaded && newVal !== oldVal && this.isInitialSyncDone && this.syncSettings.spreadsheet_id && !this.isAutoSaving) {
          this.autoSaveSettings()
        }
      }
    }
  },
  methods: {
    async loadConnectionStatus() {
      this.isLoading = true

      // Check localized script first
      const gsData = window.formychat_admin_vars?.additional?.google_sheets

      // Check if token was revoked
      if (gsData && gsData.revoked) {
        this.isRevoked = true
        this.connectedEmail = gsData.email || ''
        this.connectedPicture = gsData.picture || ''
        this.isLoading = false
        return
      }

      if (gsData && gsData.connected) {
        this.isConnected = true
        this.connectedEmail = gsData.email || ''
        this.connectedPicture = gsData.picture || ''
        this.connectionStatus = 'success'

        // Load sync data
        this.loadSyncData()

        // If no email or picture yet, fetch from auth server
        if ((!this.connectedEmail || !this.connectedPicture) && gsData.access_token) {
          this.isLoading = false
          this.fetchUserInfo(gsData.access_token, gsData.refresh_token)
          return
        }

        this.$emit('connected', { email: this.connectedEmail })
        this.isLoading = false
        return
      }

      // Fallback to API call if not in localized script
      try {
        const response = await API.get('integrations/google-sheets/status')
        if (response.data && response.data.connected) {
          this.isConnected = true
          this.connectedEmail = response.data.email || ''
          this.connectedPicture = response.data.picture || ''
          this.connectionStatus = 'success'

          // Load sync data
          this.loadSyncData()

          // If no email or picture yet, fetch from auth server
          if ((!this.connectedEmail || !this.connectedPicture) && response.data.access_token) {
            this.fetchUserInfo(response.data.access_token, response.data.refresh_token)
          }

          this.$emit('connected', { email: this.connectedEmail })
        }
      } catch (error) {
        console.log('Failed to load Google Sheets status')
      } finally {
        this.isLoading = false
      }
    },
    async refreshAccessToken(refreshToken) {
      try {
        const response = await fetch('https://auth-staging.wppool.dev/refresh/google/formychat-sheet-access', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ refresh_token: refreshToken })
        })

        if (response.ok) {
          const data = await response.json()
          if (data.access_token) {
            // Save new access token to backend
            await API.post('integrations/google-sheets/token', {
              access_token: data.access_token
            })
            return data.access_token
          }
        }
      } catch (error) {
        console.log('Failed to refresh access token:', error)
      }
      return null
    },
    async fetchUserInfo(accessToken, refreshToken = null, isRetry = false) {
      this.isFetchingUserInfo = true
      try {
        const response = await fetch('https://auth-staging.wppool.dev/userinfo/google', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ access_token: accessToken })
        })

        if (response.ok) {
          const data = await response.json()

          if (data.email || data.picture) {
            // Save to backend
            await API.post('integrations/google-sheets/userinfo', {
              email: data.email || '',
              picture: data.picture || ''
            })

            this.connectedEmail = data.email || this.connectedEmail
            this.connectedPicture = data.picture || ''
          }
        } else if (response.status === 401 && !isRetry && refreshToken) {
          // Token expired, try to refresh
          const newAccessToken = await this.refreshAccessToken(refreshToken)
          if (newAccessToken) {
            return this.fetchUserInfo(newAccessToken, null, true)
          }
        }
      } catch (error) {
        console.log('Failed to fetch user info from auth server:', error)
      } finally {
        this.isFetchingUserInfo = false
      }
    },
    handleConnect() {
      const callbackUrl = new URL(window.location.href)
      callbackUrl.searchParams.set('formychat-action', 'authenticated')
      callbackUrl.searchParams.set('integration', 'googlesheets')

      const authUrl = `https://auth-staging.wppool.dev/auth/google/formychat-sheet-access?callback_url=${encodeURIComponent(callbackUrl.toString())}&scope=openid%20email%20https://www.googleapis.com/auth/spreadsheets%20https://www.googleapis.com/auth/drive.readonly`
      window.location.href = authUrl
    },
    async handleReconnect() {
      // Clear existing data first
      try {
        await API.post('integrations/google-sheets/disconnect')
      } catch (error) {
        // Ignore error, proceed with reconnect
      }
      // Redirect to OAuth
      this.handleConnect()
    },
    async handleDisconnect() {
      this.isDisconnecting = true
      this.errorMessage = ''

      try {
        await API.post('integrations/google-sheets/disconnect')
        await API.post('integrations', {
          integration_id: 'google_sheets',
          enabled: false
        })

        // Reset connection state
        this.isConnected = false
        this.connectedEmail = ''
        this.connectedPicture = ''
        this.connectionStatus = null
        this.showRemoveConfirmation = false

        // Reset sync settings
        this.syncSettings = {
          spreadsheet_id: '',
          spreadsheet_name: '',
          sync_mode: 'scheduled',
          sync_interval: '1h',
          last_sync_at: null,
        }
        this.syncStats = {
          total: 0,
          synced: 0,
          pending: 0,
        }
        this.isInitialSyncDone = false
        this.selectedSheetId = ''
        this.newSheetName = ''
        this.showCreateMode = false
        this.isChangingSheet = false

        this.$emit('disconnected')
      } catch (error) {
        this.connectionStatus = 'error'
        this.errorMessage = error.response?.data?.message || this.t('failed_to_disconnect')
      } finally {
        this.isDisconnecting = false
      }
    },
    // Sync methods
    loadSyncData() {
      // Load all from localized script immediately
      const gsData = window.formychat_admin_vars?.additional?.google_sheets
      if (gsData) {
        if (gsData.intervals) {
          this.intervals = gsData.intervals
        }
        if (gsData.free_limit) {
          this.freeLimit = gsData.free_limit
        }
        if (gsData.sync_settings) {
          this.syncSettings = { ...this.syncSettings, ...gsData.sync_settings }
          if (gsData.sync_settings.last_sync_at) {
            this.isInitialSyncDone = true
          }
        }
        if (gsData.sync_stats) {
          this.syncStats = { ...this.syncStats, ...gsData.sync_stats }
        }
      }

      // Only fetch spreadsheets async
      if (this.isConnected) {
        this.loadSpreadsheets()
      }

      // Mark data as loaded after a tick to prevent watcher from triggering
      this.$nextTick(() => {
        this.isDataLoaded = true
      })
    },
    updateGlobalData(settings = null, stats = null) {
      if (!window.formychat_admin_vars?.additional?.google_sheets) return

      if (settings) {
        window.formychat_admin_vars.additional.google_sheets.sync_settings = {
          ...window.formychat_admin_vars.additional.google_sheets.sync_settings,
          ...settings
        }
      }
      if (stats) {
        window.formychat_admin_vars.additional.google_sheets.sync_stats = {
          ...window.formychat_admin_vars.additional.google_sheets.sync_stats,
          ...stats
        }
      }
    },
    clearSpreadsheet() {
      this.syncSettings.spreadsheet_id = ''
      this.syncSettings.spreadsheet_name = ''
      this.selectedSheetId = ''
      this.showCreateMode = false
      this.isChangingSheet = false
    },
    cancelChangeSheet() {
      this.isChangingSheet = false
      this.selectedSheetId = ''
      this.newSheetName = ''
      this.showCreateMode = false
    },
    async confirmSelectSheet() {
      if (!this.selectedSheetId) return

      const selected = this.spreadsheets.find(s => s.id === this.selectedSheetId)
      if (!selected) return

      this.isSelectingSheet = true
      this.syncError = ''

      try {
        // Just set the spreadsheet selection (no sync yet)
        this.syncSettings.spreadsheet_id = selected.id
        this.syncSettings.spreadsheet_name = selected.name
        this.selectedSheetId = ''
        this.isChangingSheet = false
        this.isInitialSyncDone = false
      } catch (error) {
        this.syncError = error.response?.data?.message || this.t('failed_to_setup_spreadsheet')
      } finally {
        this.isSelectingSheet = false
      }
    },
    async loadSpreadsheets() {
      if (this.isLoadingSheets) return

      this.isLoadingSheets = true
      this.syncError = ''
      try {
        const response = await API.get('integrations/google-sheets/spreadsheets')
        if (response.data?.spreadsheets) {
          this.spreadsheets = response.data.spreadsheets
        }
      } catch (error) {
        console.log('Failed to load spreadsheets:', error)
        this.syncError = this.t('failed_to_load_spreadsheets_error')
      } finally {
        this.isLoadingSheets = false
      }
    },
    onSpreadsheetSelect(id) {
      const selected = this.spreadsheets.find(s => s.id === id)
      if (selected) {
        this.syncSettings.spreadsheet_name = selected.name
      }
    },
    async createNewSheet() {
      if (!this.newSheetName) return

      this.isCreatingSheet = true
      this.syncError = ''

      try {
        const response = await API.post('integrations/google-sheets/spreadsheets', {
          title: this.newSheetName
        })

        if (response.data?.spreadsheet) {
          const newSheet = response.data.spreadsheet
          this.spreadsheets.unshift({ id: newSheet.id, name: newSheet.name })
          this.syncSettings.spreadsheet_id = newSheet.id
          this.syncSettings.spreadsheet_name = newSheet.name
          this.updateGlobalData({ spreadsheet_id: newSheet.id, spreadsheet_name: newSheet.name })
          this.newSheetName = ''
          this.showCreateMode = false
          this.isChangingSheet = false
          this.isInitialSyncDone = false
        }
      } catch (error) {
        this.syncError = error.response?.data?.message || this.t('failed_to_create_spreadsheet')
      } finally {
        this.isCreatingSheet = false
      }
    },
    async saveSettings() {
      this.isSaving = true
      this.settingsError = ''
      this.settingsSuccess = ''

      try {
        const response = await API.post('integrations/google-sheets/sync-settings', {
          spreadsheet_id: this.syncSettings.spreadsheet_id,
          spreadsheet_name: this.syncSettings.spreadsheet_name,
          sync_mode: this.syncSettings.sync_mode,
          sync_interval: this.syncSettings.sync_interval,
        })

        if (response.data?.settings) {
          this.syncSettings = { ...this.syncSettings, ...response.data.settings }
          this.updateGlobalData(response.data.settings)
        }
        this.settingsSuccess = this.t('settings_saved_success')
        setTimeout(() => { this.settingsSuccess = '' }, 3000)
      } catch (error) {
        this.settingsError = error.response?.data?.message || this.t('failed_to_save_settings')
      } finally {
        this.isSaving = false
      }
    },
    async autoSaveSettings() {
      this.settingsError = ''
      this.isAutoSaving = true

      try {
        const response = await API.post('integrations/google-sheets/sync-settings', {
          spreadsheet_id: this.syncSettings.spreadsheet_id,
          spreadsheet_name: this.syncSettings.spreadsheet_name,
          sync_mode: this.syncSettings.sync_mode,
          sync_interval: this.syncSettings.sync_interval,
          save_only: true,
        })
        if (response.data?.settings) {
          this.updateGlobalData(response.data.settings)
        }
      } catch (error) {
        this.settingsError = error.response?.data?.message || 'Failed to save settings.'
      } finally {
        this.isAutoSaving = false
      }
    },
    async saveAndSync() {
      this.isSaving = true
      this.isSyncing = true
      this.syncError = ''
      this.syncSuccess = ''
      this.settingsError = ''

      try {
        // First save settings
        await API.post('integrations/google-sheets/sync-settings', {
          spreadsheet_id: this.syncSettings.spreadsheet_id,
          spreadsheet_name: this.syncSettings.spreadsheet_name,
          sync_mode: this.syncSettings.sync_mode,
          sync_interval: this.syncSettings.sync_interval,
        })

        // Then do a full resync
        const response = await API.post('integrations/google-sheets/resync')

        if (response.data?.stats) {
          this.syncStats = response.data.stats
          this.updateGlobalData(null, response.data.stats)
        }
        if (response.data?.settings) {
          this.syncSettings = { ...this.syncSettings, ...response.data.settings }
          this.updateGlobalData(response.data.settings)
        }

        this.isInitialSyncDone = true
        this.syncSuccess = `Synced ${response.data?.synced ?? 0} lead(s) successfully!`
        setTimeout(() => { this.syncSuccess = '' }, 5000)
      } catch (error) {
        this.syncError = error.response?.data?.message || 'Failed to save and sync.'
      } finally {
        this.isSaving = false
        this.isSyncing = false
      }
    },
    async triggerManualSync() {
      this.isSyncing = true
      this.syncButtonState = null

      try {
        const response = await API.post('integrations/google-sheets/sync')

        if (response.data?.stats) {
          this.syncStats = response.data.stats
          this.updateGlobalData(null, response.data.stats)
        }
        if (response.data?.settings) {
          this.syncSettings = { ...this.syncSettings, ...response.data.settings }
          this.updateGlobalData(response.data.settings)
        }

        // Show synced state
        this.syncButtonState = 'synced'
        setTimeout(() => { this.syncButtonState = null }, 3000)
      } catch (error) {
        // Reset button on error
        this.syncButtonState = null
      } finally {
        this.isSyncing = false
      }
    },
    triggerFullResync() {
      this.showResyncConfirmation = true
    },
    async executeFullResync() {
      this.showResyncConfirmation = false
      this.isSyncing = true
      this.syncError = ''
      this.syncSuccess = ''

      try {
        const response = await API.post('integrations/google-sheets/resync')

        if (response.data?.stats) {
          this.syncStats = response.data.stats
          this.updateGlobalData(null, response.data.stats)
        }

        if (response.data?.success) {
          this.syncSuccess = `Full resync complete! Synced ${response.data.synced} lead(s).`
          setTimeout(() => { this.syncSuccess = '' }, 3000)
        } else if (response.data?.error) {
          this.syncError = response.data.error
        }
      } catch (error) {
        this.syncError = error.response?.data?.message || 'Resync failed.'
      } finally {
        this.isSyncing = false
      }
    },
    async loadSyncStats() {
      try {
        const response = await API.get('integrations/google-sheets/sync-status')
        if (response.data?.stats) {
          this.syncStats = response.data.stats
        }
      } catch (error) {
        console.log('Failed to load sync stats:', error)
      }
    }
  }
}
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
